import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Box, MenuItem, IconButton,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Chip, CircularProgress, Select, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from '@mui/material';
import {
  Calculate, Print,
  CheckCircle, Delete,
} from '@mui/icons-material';
import { authService, salarySlipService, printSettingsService } from '../../services/api';
import TopBar from '../../components/TopBar';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const fmt2 = n => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

export default function SalaryProcessingPage() {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year,  setYear]  = useState(now.getFullYear());

  const [slips,    setSlips]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genResult,  setGenResult]  = useState(null);
  const [printSettings, setPrintSettings] = useState(null);

  const [deleteId,   setDeleteId]   = useState(null);
  const [markPaidId, setMarkPaidId] = useState(null);

  const loadSlips = useCallback(async () => {
    setLoading(true);
    try {
      const res = await salarySlipService.getAll({ month, year });
      setSlips(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [month, year]);

  useEffect(() => { loadSlips(); }, [loadSlips]);
  useEffect(() => {
    printSettingsService.getSettings().then(r => setPrintSettings(r.data)).catch(() => {});
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenResult(null);
    try {
      const res = await salarySlipService.generate({ month, year });
      setGenResult({ success: true, message: res.data.message });
      await loadSlips();
    } catch (err) {
      setGenResult({ success: false, message: 'Failed to generate salary slips.' });
    } finally { setGenerating(false); }
  };

  const handleMarkPaid = async () => {
    try {
      await salarySlipService.markPaid(markPaidId);
      setMarkPaidId(null);
      await loadSlips();
    } catch { /* ignore */ }
  };

  const handleDelete = async () => {
    try {
      await salarySlipService.delete(deleteId);
      setDeleteId(null);
      await loadSlips();
    } catch { /* ignore */ }
  };

  const getPrintConfig = () => {
    const hospital    = printSettings?.hospitalName || printSettings?.HospitalName || 'Hospital';
    const address     = printSettings?.address      || printSettings?.Address      || '';
    const phone       = printSettings?.phone        || printSettings?.Phone        || '';
    const mt = printSettings?.marginTop    ?? printSettings?.MarginTop    ?? 10;
    const mb = printSettings?.marginBottom ?? printSettings?.MarginBottom ?? 10;
    const ml = printSettings?.marginLeft   ?? printSettings?.MarginLeft   ?? 10;
    const mr = printSettings?.marginRight  ?? printSettings?.MarginRight  ?? 10;
    const isPrePrinted = (printSettings?.letterheadType || printSettings?.LetterheadType || 'Pre-Printed Letterhead') === 'Pre-Printed Letterhead';
    return { hospital, address, phone, mt, mb, ml, mr, isPrePrinted };
  };

  const buildSlipBody = (slip, cfg) => `
    <div class="slip-page">
      ${!cfg.isPrePrinted ? `<h2>${cfg.hospital}</h2><p class="sub">${cfg.address}${cfg.phone ? ' | ' + cfg.phone : ''}</p>` : ''}
      <h2 style="margin-top:${cfg.isPrePrinted ? 0 : 8}px">SALARY SLIP</h2>
      <p class="sub">${MONTHS[slip.month - 1]} ${slip.year}</p>
      <hr/>
      <table>
        <tr><th>Employee Name</th><td>${slip.userName}</td><th>Designation</th><td>${slip.role}</td></tr>
        <tr><th>Month / Year</th><td>${MONTHS[slip.month - 1]} ${slip.year}</td><th>Payment Status</th><td>${slip.isPaid ? 'Paid' : 'Pending'}</td></tr>
        <tr><th>Working Days</th><td>${slip.workingDays}</td><th>Present Days</th><td>${slip.presentDays}</td></tr>
        <tr><th>Paid Leave Days</th><td>${slip.leaveDays}</td><th>Absent Days</th><td>${slip.absentDays}</td></tr>
      </table>
      <table style="margin-top:16px">
        <tr><th>Earnings</th><th class="right">Amount (₹)</th><th>Deductions</th><th class="right">Amount (₹)</th></tr>
        <tr>
          <td>Gross Salary</td><td class="right">${fmt2(slip.grossSalary)}</td>
          <td>Absence Deduction</td><td class="right">${fmt2(slip.absenceDeduction)}</td>
        </tr>
        <tr>
          <td></td><td></td>
          <td>Fixed Deductions</td><td class="right">${fmt2(slip.fixedDeductions)}</td>
        </tr>
        <tr class="total">
          <td>Total Earnings</td><td class="right">${fmt2(slip.grossSalary)}</td>
          <td>Total Deductions</td><td class="right">${fmt2((slip.absenceDeduction || 0) + (slip.fixedDeductions || 0))}</td>
        </tr>
        <tr class="net">
          <td colspan="3">NET SALARY PAYABLE</td>
          <td class="right">₹ ${fmt2(slip.netSalary)}</td>
        </tr>
      </table>
      <div class="footer">
        <div class="sign-block"><div class="sign-line">Employee Signature</div></div>
        <div class="sign-block"><div class="sign-line">Authorised Signatory</div></div>
      </div>
    </div>
  `;

  const openPrintWindow = (title, bodyHtml, cfg) => {
    const win = window.open('', '_blank');
    if (!win) { alert('Popup blocked. Please allow popups for this site and try again.'); return; }
    win.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: ${cfg.mt}mm ${cfg.mr}mm ${cfg.mb}mm ${cfg.ml}mm; font-size: 13px; color: #000; }
            h2 { text-align: center; margin: 0 0 4px 0; }
            .sub { text-align: center; color: #333; margin-bottom: 16px; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            td, th { padding: 6px 10px; border: 1px solid #ccc; }
            th { background: #f5f5f5; text-align: left; }
            .total td { font-weight: bold; background: #e8f5e9; }
            .right { text-align: right; }
            .net td { font-weight: bold; font-size: 15px; background: #1565c0; color: #fff; }
            .footer { margin-top: 40px; display: flex; justify-content: space-between; }
            .sign-block { text-align: center; width: 200px; }
            .sign-line { border-top: 1px solid #000; margin-top: 36px; padding-top: 4px; font-size: 12px; }
            .slip-page { page-break-after: always; }
            .slip-page:last-child { page-break-after: avoid; }
          </style>
        </head>
        <body>${bodyHtml}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const handlePrintSlip = (slip) => {
    const cfg = getPrintConfig();
    openPrintWindow(
      `Salary Slip — ${slip.userName} — ${MONTHS[slip.month - 1]} ${slip.year}`,
      buildSlipBody(slip, cfg),
      cfg
    );
  };

  const handlePrintAll = () => {
    const cfg = getPrintConfig();
    const allBodies = slips.map(s => buildSlipBody(s, cfg)).join('');
    openPrintWindow(
      `Salary Slips — ${MONTHS[month - 1]} ${year}`,
      allBodies,
      cfg
    );
  };

  const totalNet = slips.reduce((sum, s) => sum + (s.netSalary || 0), 0);
  const totalGross = slips.reduce((sum, s) => sum + (s.grossSalary || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Salary Processing" onBack={() => navigate('/dashboard')} />

      <Box sx={{ p: 3 }}>
        {/* Controls */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={e => setMonth(e.target.value)}>
                {MONTHS.map((m, i) => <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={e => setYear(e.target.value)}>
                {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <Calculate />}
              onClick={handleGenerate}
              disabled={generating}
              sx={{ background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)' }}
            >
              Generate / Recalculate
            </Button>

            {slips.length > 0 && (
              <Button
                variant="outlined"
                startIcon={<Print />}
                onClick={handlePrintAll}
              >
                Print All Slips
              </Button>
            )}
          </Box>

          {genResult && (
            <Alert severity={genResult.success ? 'success' : 'error'} sx={{ mt: 2 }}
              onClose={() => setGenResult(null)}>
              {genResult.message}
            </Alert>
          )}
        </Paper>

        {/* Summary */}
        {slips.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Paper elevation={1} sx={{ p: 1.5, px: 2.5, borderRadius: 2, borderLeft: '4px solid #1565c0' }}>
              <Typography variant="caption" color="text.secondary">Total Staff</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{slips.length}</Typography>
            </Paper>
            <Paper elevation={1} sx={{ p: 1.5, px: 2.5, borderRadius: 2, borderLeft: '4px solid #43a047' }}>
              <Typography variant="caption" color="text.secondary">Total Gross</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>₹ {fmt2(totalGross)}</Typography>
            </Paper>
            <Paper elevation={1} sx={{ p: 1.5, px: 2.5, borderRadius: 2, borderLeft: '4px solid #2e7d32' }}>
              <Typography variant="caption" color="text.secondary">Total Net Payable</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.dark' }}>₹ {fmt2(totalNet)}</Typography>
            </Paper>
            <Paper elevation={1} sx={{ p: 1.5, px: 2.5, borderRadius: 2, borderLeft: '4px solid #4caf50' }}>
              <Typography variant="caption" color="text.secondary">Paid</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                {slips.filter(s => s.isPaid).length} / {slips.length}
              </Typography>
            </Paper>
          </Box>
        )}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#1565c0' }}>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Staff Name</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Role</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Work Days</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Present</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Leave</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Absent</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Gross (₹)</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Deductions (₹)</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Net (₹)</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Status</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {slips.map((s, idx) => (
                    <TableRow key={s.id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{s.userName}</TableCell>
                      <TableCell>
                        <Chip label={s.role} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">{s.workingDays}</TableCell>
                      <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>{s.presentDays}</TableCell>
                      <TableCell align="center" sx={{ color: 'primary.main', fontWeight: 600 }}>{s.leaveDays}</TableCell>
                      <TableCell align="center" sx={{ color: 'error.main', fontWeight: 600 }}>{s.absentDays}</TableCell>
                      <TableCell align="right">{fmt2(s.grossSalary)}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        {fmt2((s.absenceDeduction || 0) + (s.fixedDeductions || 0))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'success.dark' }}>
                        {fmt2(s.netSalary)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={s.isPaid ? 'Paid' : 'Pending'}
                          color={s.isPaid ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Print fontSize="small" />}
                            onClick={() => handlePrintSlip(s)}
                            sx={{ minWidth: 60, px: 1 }}
                          >
                            Print
                          </Button>
                          {!s.isPaid && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<CheckCircle fontSize="small" />}
                              onClick={() => setMarkPaidId(s.id)}
                              sx={{ minWidth: 60, px: 1 }}
                            >
                              Paid
                            </Button>
                          )}
                          <IconButton size="small" color="error" onClick={() => setDeleteId(s.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {slips.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        No salary slips for {MONTHS[month - 1]} {year}. Click "Generate / Recalculate" to create them.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      {/* Mark Paid Confirm */}
      <Dialog open={!!markPaidId} onClose={() => setMarkPaidId(null)}>
        <DialogTitle>Mark Salary as Paid</DialogTitle>
        <DialogContent>Confirm that salary has been disbursed for this staff member?</DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidId(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleMarkPaid}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Salary Slip</DialogTitle>
        <DialogContent>Are you sure you want to delete this salary slip?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
